import { useBreakpoints } from '@sunwebgroup/ui-components/uds'
import {
  add,
  differenceInDays,
  format,
  getDay,
  isAfter,
  isBefore,
  isEqual,
  isSameDay,
  lastDayOfMonth,
  max,
  nextMonday,
  parseISO,
  previousMonday,
  previousSunday,
  sub,
} from 'date-fns'
import { createContext, PropsWithChildren, use, useCallback, useEffect, useMemo, useState } from 'react'
import { SwiperClass } from 'swiper/react'
import { getPriceRangeSelector, isFirstMonthSelector } from '@/scopes/prices/components/selectors'
import { PricesActionsContext } from '@/scopes/prices/domain/PricesActionsContext'
import { usePricesSelector } from '@/scopes/prices/infrastructure/usePricesSelector'

export interface NavigationByWeeksContextType {
  isNavigationLeftEnabled?: boolean
  isNavigationRightEnabled?: boolean
  navigationDateFrom?: Date
  navigationDateTo?: Date
  navigateRight: (date?: Date) => void
  navigateLeft: (date?: Date) => void
  navigateToMonth: (startDate: Date) => void
  focusedDate?: Date
  updateFocusedDate: (date: Date) => void
  swiperInstance?: SwiperClass | null
  setSwiper: (instance: SwiperClass | null) => void
  focusedSlide: number
  setFocusedSlide: (slide: number) => void
  datesToDisplay: string[]
  setDatesToDisplay: (dates: string[]) => void
  numberOfColumns: number
  reset: () => void
}

export const NavigationByWeeksContext = createContext<NavigationByWeeksContextType>({
  isNavigationLeftEnabled: false,
  isNavigationRightEnabled: false,
  navigationDateFrom: undefined,
  navigationDateTo: undefined,
  navigateRight: () => {},
  navigateLeft: () => {},
  navigateToMonth: () => {},
  focusedDate: undefined,
  updateFocusedDate: () => {},
  swiperInstance: undefined,
  setSwiper: () => {},
  focusedSlide: 0,
  setFocusedSlide: () => {},
  datesToDisplay: [],
  setDatesToDisplay: () => {},
  numberOfColumns: 7,
  reset: () => {},
})

enum ColumnsLengthLimits {
  DESKTOP = 7,
  TABLET = 5,
  MOBILE = 3,
}

export function NavigationByWeeksContextProvider({ children }: PropsWithChildren) {
  const {
    dateFrom,
    dateTo,
    options: months,
    minDatePrices,
    maxDatePrices,
    minPriceDate,
    selectedPriceDate,
    promotedPriceDate,
    initialFocusDate,
  } = usePricesSelector(getPriceRangeSelector)
  const { isFirstMonth } = usePricesSelector(isFirstMonthSelector)
  const { selectRange } = use(PricesActionsContext)
  const { isTabletOrGreater, isDesktop, isMobileOrGreater } = useBreakpoints()
  const [numberOfColumns, setNumberOfColumns] = useState<ColumnsLengthLimits>(ColumnsLengthLimits.DESKTOP)

  useEffect(() => {
    let newLimit = ColumnsLengthLimits.DESKTOP

    if (isDesktop) {
      newLimit = ColumnsLengthLimits.DESKTOP
    }
    else if (isTabletOrGreater) {
      newLimit = ColumnsLengthLimits.TABLET
    }
    else if (isMobileOrGreater) {
      newLimit = ColumnsLengthLimits.MOBILE
    }

    setNumberOfColumns(newLimit)
  }, [isMobileOrGreater, isTabletOrGreater, isDesktop])

  const [navigationDateFrom, setLocalNavigationDateFrom] = useState<Date>()
  const [navigationDateTo, setLocalNavigationDateTo] = useState<Date>()
  const [focusedDate, setFocusedDate] = useState<Date>()
  const [swiperInstance, setSwiper] = useState<SwiperClass | null>(null)
  const [focusedSlide, setFocusedSlide] = useState(0)
  const [datesToDisplay, setDatesToDisplay] = useState<string[]>([])

  useEffect(() => {
    if (!dateFrom || !minPriceDate || navigationDateFrom) {
      return
    }

    const { to, from, newFocusedDate } = recalculatePriceTableParams(dateFrom)
    setLocalNavigationDateFrom(from)
    setLocalNavigationDateTo(to)
    setFocusedDate(newFocusedDate)
  }, [dateFrom, minPriceDate, selectedPriceDate, promotedPriceDate, initialFocusDate])

  useEffect(() => {
    if (!dateFrom || !minPriceDate) {
      return
    }

    const { to, from, newFocusedDate } = recalculatePriceTableParams(dateFrom)
    setLocalNavigationDateFrom(from)
    setLocalNavigationDateTo(to)
    setFocusedDate(newFocusedDate)
  }, [numberOfColumns])

  const resetNavigation = useCallback(() => {
    setLocalNavigationDateFrom(undefined)
    setLocalNavigationDateTo(undefined)
  }, [])

  const getPreviousMonday = useCallback((fromDate = new Date()) => {
    if (numberOfColumns < ColumnsLengthLimits.DESKTOP) {
      return fromDate
    }
    return getDay(fromDate) === 1 ? fromDate : previousMonday(fromDate)
  }, [numberOfColumns])

  function recalculatePriceTableParams(dateFrom: string) {
    const newFocusedDate = new Date(initialFocusDate)
    const mondayOfFirstWeekWithPrices = getPreviousMonday(newFocusedDate)
    const from = max([new Date(dateFrom), mondayOfFirstWeekWithPrices])
    const to = add(from, { days: numberOfColumns - 1 })

    return { newFocusedDate, mondayOfFirstWeekWithPrices, from, to }
  }

  const isNavigationLeftEnabled = useMemo(() => {
    if (!isTabletOrGreater) {
      if (!focusedDate)
        return false

      if (!isFirstMonth(format(focusedDate, 'yyyy-MM-dd'))) {
        return true
      }

      const previousFocusedDate = sub(focusedDate, { days: numberOfColumns })
      return (
        isAfter(previousFocusedDate, new Date(minDatePrices))
        || isEqual(previousFocusedDate, new Date(minDatePrices))
      )
    }

    if (!navigationDateTo)
      return false

    if (!isFirstMonth(format(navigationDateTo, 'yyyy-MM-dd'))) {
      return true
    }

    const previousNavigationDateTo
      = numberOfColumns < ColumnsLengthLimits.DESKTOP
        ? sub(navigationDateTo, { days: numberOfColumns })
        : previousSunday(navigationDateTo)
    return (
      isAfter(previousNavigationDateTo, new Date(minDatePrices))
      || isEqual(previousNavigationDateTo, new Date(minDatePrices))
    )
  }, [navigationDateTo, minDatePrices, months])

  const isNavigationRightEnabled = useMemo(() => {
    const isItLastMonth = (date: Date) => {
      const monthValues = Object.values(months).flat()
      if (monthValues.length === 0) {
        return false
      }
      return monthValues[monthValues.length - 1].value === format(date, 'yyyy-MM')
    }

    if (!isTabletOrGreater) {
      if (!focusedDate)
        return false

      if (!isItLastMonth(focusedDate)) {
        return true
      }

      const nextFocusedDate = add(focusedDate, { days: numberOfColumns })
      return (
        isBefore(nextFocusedDate, new Date(maxDatePrices))
        || isSameDay(nextFocusedDate, new Date(maxDatePrices))
      )
    }

    if (!navigationDateFrom)
      return false

    if (!isItLastMonth(navigationDateFrom)) {
      return true
    }

    const nextNavigationDateFrom
      = numberOfColumns < ColumnsLengthLimits.DESKTOP
        ? add(navigationDateFrom, { days: numberOfColumns })
        : nextMonday(navigationDateFrom)

    return (
      isBefore(nextNavigationDateFrom, new Date(maxDatePrices))
      || isSameDay(nextNavigationDateFrom, new Date(maxDatePrices))
    )
  }, [navigationDateFrom, maxDatePrices, months])

  const navigateToMonth = useCallback((startDate?: Date) => {
    if (!startDate) {
      return
    }
    const firstMonthMonday = getPreviousMonday(startDate)
    const newFocusedDate = add(startDate, { days: 1 })
    setFocusedDate(newFocusedDate)
    setLocalNavigationDateFrom(firstMonthMonday)
    setLocalNavigationDateTo(add(firstMonthMonday, { days: numberOfColumns - 1 }))
  }, [getPreviousMonday, numberOfColumns])

  const navigateRight = useCallback((date?: Date) => {
    if (!navigationDateFrom || !navigationDateTo || !dateTo)
      return

    if (!isTabletOrGreater) {
      if (!focusedDate) {
        setFocusedDate(parseISO(initialFocusDate))
      }
      else {
        let newFocusedDate = date ? add(date, { days: 2 }) : add(focusedDate, { days: 3 })
        const currentMonth = format(focusedDate, 'yyyy-MM')
        const lastDayOfCurrentMonth = lastDayOfMonth(parseISO(`${currentMonth}-01`))

        const difference = differenceInDays(lastDayOfCurrentMonth, focusedDate)

        if (difference === 2 || difference === 3) {
          newFocusedDate = add(focusedDate, { days: difference - 1 })
        }
        setFocusedDate(newFocusedDate)

        if (isAfter(newFocusedDate, lastDayOfCurrentMonth) || isSameDay(newFocusedDate, lastDayOfCurrentMonth)) {
          setFocusedSlide(datesToDisplay.length - 1)
          const from = format(newFocusedDate, 'yyyy-MM-01')
          const to = format(lastDayOfMonth(newFocusedDate), 'yyyy-MM-dd')
          const month = format(newFocusedDate, 'yyyy-MM')
          selectRange({ dateFrom: from, dateTo: to, month })
        }
      }
    }
    else {
      const newDateFrom = add(navigationDateFrom, { days: numberOfColumns })
      const newDateTo = add(navigationDateTo, { days: numberOfColumns })
      setLocalNavigationDateFrom(newDateFrom)
      setLocalNavigationDateTo(newDateTo)
      if (isAfter(newDateTo, parseISO(dateTo)) || isSameDay(newDateTo, parseISO(dateTo))) {
        const from = format(newDateTo, 'yyyy-MM-01')
        const to = format(lastDayOfMonth(newDateTo), 'yyyy-MM-dd')
        const month = format(newDateTo, 'yyyy-MM')
        selectRange({ dateFrom: from, dateTo: to, month })
      }
    }
  }, [navigationDateFrom, navigationDateTo, dateTo, isTabletOrGreater, focusedDate, initialFocusDate, datesToDisplay.length, selectRange, numberOfColumns])

  const navigateLeft = useCallback((date?: Date) => {
    if (!navigationDateFrom || !navigationDateTo || !dateFrom)
      return

    if (!isTabletOrGreater && focusedDate) {
      let newFocusedDate = date ? sub(date, { days: 2 }) : sub(focusedDate, { days: 3 })
      const currentMonth = format(focusedDate, 'yyyy-MM')

      const difference = differenceInDays(focusedDate, parseISO(`${currentMonth}-01`))

      if (difference === 2 || difference === 3) {
        newFocusedDate = sub(focusedDate, { days: difference - 1 })
      }

      setFocusedDate(newFocusedDate)

      if (isBefore(newFocusedDate, parseISO(`${currentMonth}-01`)) || isSameDay(newFocusedDate, parseISO(`${currentMonth}-01`))) {
        setFocusedSlide(0)
        const from = format(newFocusedDate, 'yyyy-MM-01')
        const to = format(lastDayOfMonth(newFocusedDate), 'yyyy-MM-dd')
        const month = format(newFocusedDate, 'yyyy-MM')
        selectRange({ dateFrom: from, dateTo: to, month })
      }
    }
    else {
      const newDateFrom = sub(navigationDateFrom, { days: numberOfColumns })
      const newDateTo = sub(navigationDateTo, { days: numberOfColumns })
      setLocalNavigationDateFrom(newDateFrom)
      setLocalNavigationDateTo(newDateTo)
      if (isBefore(newDateFrom, parseISO(dateFrom)) || isSameDay(newDateFrom, parseISO(dateFrom))) {
        const from = format(newDateFrom, 'yyyy-MM-01')
        const to = format(lastDayOfMonth(newDateFrom), 'yyyy-MM-dd')
        const month = format(newDateFrom, 'yyyy-MM')
        selectRange({ dateFrom: from, dateTo: to, month })
      }
    }
  }, [navigationDateFrom, navigationDateTo, dateFrom, isTabletOrGreater, focusedDate, selectRange, numberOfColumns])

  const updateFocusedDate = useCallback((date: Date) => {
    setFocusedDate(date)
  }, [])

  const value = useMemo(
    () => ({
      navigationDateFrom,
      navigationDateTo,
      isNavigationLeftEnabled,
      isNavigationRightEnabled,
      navigateRight,
      navigateLeft,
      navigateToMonth,
      focusedDate,
      updateFocusedDate,
      setSwiper,
      swiperInstance,
      focusedSlide,
      setFocusedSlide,
      datesToDisplay,
      setDatesToDisplay,
      numberOfColumns,
      reset: resetNavigation,
    }),
    [
      navigationDateFrom,
      navigationDateTo,
      isNavigationLeftEnabled,
      isNavigationRightEnabled,
      navigateRight,
      navigateLeft,
      navigateToMonth,
      focusedDate,
      updateFocusedDate,
      swiperInstance,
      focusedSlide,
      datesToDisplay,
      numberOfColumns,
      resetNavigation,
    ],
  )

  return (
    <NavigationByWeeksContext value={value}>
      {children}
    </NavigationByWeeksContext>
  )
}
